import isEqual from 'lodash/isEqual';
import {
    createWithdrawnEl,
    removeWithdrawnEl,
    normalizeStateForSelection,
    updatePreviousOdds,
    generateButtons,
    updateSelectionWithEventDetails,
} from './utils';
import { getOddsFormat } from '../../../shared/utils/user';
import { FRACTION_ODDS_FORMAT } from '../../../shared/utils/constants';
import {
    WITHDRAWN_LABEL,
    HORSE_RACING_SELECTOR_TAG,
    HORSE_RACING_SELECTOR_ATTR,
    BETFINDER,
    HORSE_RACING_CAST_MARKET,
    HORSE_RACING_CAST_PRICE,
    HORSE_RACING_CAST_WITHDRAWN_LABEL,
    GTM_CLICK_EVENT,
} from './constants';
import { SP_ONLY_CODE } from '../../../shared/components/sport-event/constants';
import { decimalPriceOdds } from '~shared/utils/selections';

/**
 * {Map} SelectionList List of selections
 */
export const selectionsList = new Map();
export const selectionsContainerDataList = new Map();

/**
 * It initiates the Selection components for horse racing events
 *
 * @param {Object} eventData event data object
 * @param {String} selector Selection selector
 * @param {String} context context of data origin
 * @return {void}
 */
export const horseRacingInit = (eventData, selector, context = 'horse-racing') => {
    if (eventData.events_selections) {
        const selectionList = eventData.events_selections[eventData.default_event_id];
        selectionList.forEach((selectionId) => {
            const { selections_details: {
                    [selectionId]: selection,
                },
            } = eventData;
            const updatedSelection = updateSelectionWithEventDetails(selection, eventData.events[selection.event_id]);
            const normalizedSelection = normalizeStateForSelection(updatedSelection);
            const selectionSelector = `${selector} ${HORSE_RACING_SELECTOR_TAG}[${HORSE_RACING_SELECTOR_ATTR}='${selectionId}']`;
            const betButtonEl = document.querySelector(selectionSelector);

            if (betButtonEl) {
                generateButtons(betButtonEl, normalizedSelection, context, GTM_CLICK_EVENT);
            }
        });
    }
};

/**
 * It initiates the Selection components for BetFinder events
 *
 * @param {Object} eventData event data object
 * @param {String} selector Selection selector
 * @param {String} context context of data origin
 * @param {String} gtmSource source for gtm click event
 * @return {void}
 */
export const betfinderInit = (eventData, selector, context, gtmSource) => {
    if (eventData) {
        const selectionList = eventData;
        selectionList.forEach((selection) => {
            const normalizedSelection = normalizeStateForSelection({...selection, sport_slug: 'horse-racing'});
            const selectionSelector = `${selector}[${HORSE_RACING_SELECTOR_ATTR}="${selection.selection_id}"]`;
            const betButtonEl = document.querySelector(selectionSelector);

            if (betButtonEl) {
                betButtonEl.innerHTML = '';
                generateButtons(betButtonEl, normalizedSelection, context, gtmSource);
            }
        });
    }
};

/**
 * @TODO Split the function into small testable functions. The function manages different contexts
 * and handles Numeric validations which make difficult its testing. Then these small functions can be
 * moved to a Utils file.
 */

/**
 * Update a Horse Racing selection based on a Web Socket message
 * Not used by the racecard any more is by bet_finder
 *
 * @param {object} eventData Data related to the event, market and selection
 * @param {String} selector Selector of a selection
 * @param {Function} onCreateWithdrawn Callback for the creation of a Withdrawn element
 * @param {String} context context of data origin
 * @param {Function} updateBetfinderSelection updates selection in betfinder
 * @return {boolean} false
 */
export const updateHorseRacingSelection = (eventData, selector, onCreateWithdrawn = null, context = 'horse-racing', updateBetfinderSelection) => {
    let result = true;
    const events = eventData.events;
    const eventsMapping = eventData.events.reduce((acc, item) => {
        return {...acc, [item.id]: item };
    }, {});

    if (!events) {
        return false;
    }
    events.forEach((event)=> {
        const {id: event_id } = event;
        if (event.markets) {
            event.markets.forEach((market) => {
                Object.keys(market.selections).forEach((selectionId) => {
                    const id = parseInt(selectionId, 10);
                    const selectionData = market.selections[id];
                    const updatedSelectionData = updateSelectionWithEventDetails(selectionData, eventsMapping[event_id]);
                    const normalizedSelection = normalizeStateForSelection({
                        ...updatedSelectionData,
                        event_id,
                        sport_slug: context === 'trotting' ? 'trotting' : selectionData.sport_slug || context,
                        id,
                    });
                    const selection = selectionsList.get(`${context}_${id}`);
                    const currentSelectionData = selectionsContainerDataList.get(`${context}_${id}`);
                    if (selection) {
                        if (!isEqual(currentSelectionData, normalizedSelection)) {
                            selectionsContainerDataList.set(`${context}_${id}`, normalizedSelection);
                            let selectionSelector;

                            if (context === BETFINDER) {
                                selectionSelector = `${selector}[${HORSE_RACING_SELECTOR_ATTR}='${id}']`;
                            } else {
                                selectionSelector = `${selector} ${HORSE_RACING_SELECTOR_TAG}[${HORSE_RACING_SELECTOR_ATTR}='${id}']`;
                            }

                            const betButtonEl = document.querySelector(selectionSelector);

                            if (!betButtonEl) {
                                return false;
                            }

                            const castSelectionSelector = `.${HORSE_RACING_CAST_MARKET}[${HORSE_RACING_SELECTOR_ATTR}='${id}']`;
                            const castSelectionEl = document.querySelector(castSelectionSelector);

                            if (normalizedSelection.setWithdrawn) {
                                if (context === BETFINDER) {
                                    updateBetfinderSelection(selectionId);
                                } else {
                                    selection.hide();
                                    const parent = betButtonEl.parentElement;
                                    const selectorEl = `${WITHDRAWN_LABEL} ${WITHDRAWN_LABEL}-${id}`;
                                    createWithdrawnEl(parent, 'Withdrawn', selectorEl, id, onCreateWithdrawn);

                                    if (castSelectionEl) {
                                        castSelectionEl.querySelector(`.${HORSE_RACING_CAST_PRICE}`).innerHTML = HORSE_RACING_CAST_WITHDRAWN_LABEL;
                                        castSelectionEl.querySelectorAll('.checkbox-container').forEach(cb => cb.remove());
                                    }
                                }
                            } else {
                                removeWithdrawnEl(betButtonEl.parentElement, WITHDRAWN_LABEL);
                                // update past odds
                                const isFractional = getOddsFormat() === FRACTION_ODDS_FORMAT;
                                const currFractionPrice = selection.currentValues.fraction_price;
                                const currDecimalPrice = selection.currentValues.decimal_price === SP_ONLY_CODE ?
                                    selection.currentValues.decimal_price :
                                    decimalPriceOdds(selection.currentValues.decimal_price);
                                const oldSelectionPrice = isFractional ? currFractionPrice : currDecimalPrice;
                                const updatePrice = isFractional ? normalizedSelection.fraction_price :
                                    decimalPriceOdds(normalizedSelection.decimal_price);
                                if(updatePrice !== oldSelectionPrice) {
                                    updatePreviousOdds(id, oldSelectionPrice);
                                }
                                selection.show();

                                const spOnlyUpdate = normalizedSelection.decimal_price === SP_ONLY_CODE;

                                if (castSelectionEl) {
                                    const castUpdatePrice = spOnlyUpdate ? SP_ONLY_CODE : updatePrice;
                                    castSelectionEl.querySelector(`.${HORSE_RACING_CAST_PRICE}`).innerHTML = castUpdatePrice;
                                }

                                if (spOnlyUpdate) {
                                    normalizedSelection.decimal_price = currentSelectionData.decimal_price;
                                    normalizedSelection.fraction_price = currentSelectionData.fraction_price;
                                }

                                selection.update(normalizedSelection, spOnlyUpdate);
                            }
                        } else {
                            result = false;
                        }
                    } else {
                        result = false;
                    }

                    return true;
                });
            });
        } else {
            result = false;
        }
    });

    return result;
};
